<template>
  <modal :show="modalVisible" size="xl">

    <h2 class="ml-2">Select Files to Provision</h2>

    <div class="p-2">

      <!-- Device info card -->
<div v-if="mode === 'provision'" class="card bg-secondary border shadow-none mb-3">
        <div class="card-body py-3">
          <b-row>
            <b-col md="6">
              <div class="d-flex align-items-center">
                <i class="fas fa-microchip text-info mr-2"></i>
                <div>
                  <small class="text-muted font-weight-bold d-block">Device ID</small>
                  <small class="text-dark font-weight-bold ml-2">{{ deviceId }}</small>
                </div>
              </div>
            </b-col>
            <b-col md="6">
              <div class="d-flex align-items-center">
                <i class="fas fa-file-alt text-info mr-2"></i>
                <div>
                  <small class="text-muted font-weight-bold d-block">Files Selected</small>
                  <small class="text-dark font-weight-bold ml-2">
                    {{ selectedFiles.length }} / {{ files.length }}
                  </small>
                </div>
              </div>
            </b-col>
          </b-row>
        </div>
      </div>

      <!-- Loading -->
      <div v-if="loading" class="text-center py-5">
        <i class="fas fa-sync-alt fa-spin fa-2x text-primary"></i>
        <p class="mt-3 text-muted">Loading available files...</p>
      </div>

      <!-- Error -->
      <div v-else-if="fetchError" class="card border border-danger shadow-none">
        <div class="card-body py-3 text-danger">
          <i class="fas fa-exclamation-triangle mr-2"></i>{{ fetchError }}
        </div>
      </div>

      <!-- File list -->
      <div v-else-if="files.length > 0">
        <div class="card border shadow-none mb-0">
          <div class="card-header bg-secondary border py-2">
            <div class="d-flex justify-content-between align-items-center">
              <div class="custom-control custom-checkbox">
                <input
                  type="checkbox"
                  class="custom-control-input"
                  id="selectAll"
                  :checked="allSelected"
                  :indeterminate.prop="someSelected && !allSelected"
                  @change="toggleAll"
                />
                <label class="custom-control-label font-weight-bold text-dark" for="selectAll">
                  <i class="fas fa-folder-open text-muted mr-2"></i>
                  Select All Files
                </label>
              </div>
              <small class="text-muted">{{ selectedFiles.length }} of {{ files.length }} selected</small>
            </div>
          </div>
          <div class="card-body p-0 border-left border-right" style="max-height: 340px; overflow-y: auto;">
            <div
              v-for="(file, index) in files"
              :key="file.local_file"
              class="d-flex align-items-center px-3 py-2 border-bottom file-row"
              :class="{ 'bg-white': index % 2 === 0, 'selected-row': isSelected(file.local_file) }"
              @click="toggleFile(file.local_file)"
              style="cursor: pointer;"
            >
              <div class="custom-control custom-checkbox mr-3" @click.stop>
                <input
                  type="checkbox"
                  class="custom-control-input"
                  :id="`file-${index}`"
                  :checked="isSelected(file.local_file)"
                  @change="toggleFile(file.local_file)"
                />
                <label class="custom-control-label" :for="`file-${index}`"></label>
              </div>
              <i class="fas fa-file mr-2" :class="file.is_empty ? 'text-warning' : 'text-muted'"></i>
              <div class="flex-grow-1">
                <div class="small font-monospace font-weight-bold text-dark">{{ file.local_file }}</div>
                <div class="d-flex align-items-center mt-1" style="gap: 6px;">
                  <span v-if="file.is_empty === true" class="badge badge-danger" title="File is 0 bytes — will fail on device">
                    <i class="fas fa-exclamation-circle mr-1"></i>Empty — won't provision
                  </span>
                  <span v-else-if="file.file_size !== null && file.file_size !== undefined && file.file_size < 10" class="badge badge-warning" title="File is suspiciously small — may be empty content">
                    <i class="fas fa-exclamation-triangle mr-1"></i>{{ formatSize(file.file_size) }} — possibly empty
                  </span>
                  <span v-else-if="file.file_size !== null && file.file_size !== undefined" class="badge badge-light text-muted">
                    {{ formatSize(file.file_size) }}
                  </span>
                  <span v-else class="badge badge-light text-muted" title="File size could not be determined">
                    <i class="fas fa-question-circle mr-1"></i>Unknown size
                  </span>
                </div>
              </div>
              <span class="badge ml-3" :class="isSelected(file.local_file) ? 'badge-primary' : 'badge-light text-muted'">
                <i :class="isSelected(file.local_file) ? 'fas fa-check' : 'fas fa-minus'" class="mr-1"></i>
                {{ isSelected(file.local_file) ? 'Selected' : 'Skipped' }}
              </span>
            </div>
          </div>
        </div>
      </div>

      <!-- Empty -->
      <div v-else class="text-center py-5 text-muted">
        <i class="fas fa-folder-open fa-2x mb-3"></i>
        <p>No files available for this device.</p>
      </div>

    </div>

    <!-- Action buttons — inside body, footer slot used only to suppress default Cancel/OK -->
    <div class="px-3 pb-3 d-flex justify-content-end" style="gap: 8px;">
      <button type="button" class="btn btn-secondary" @click="handleClose">
        <i class="fas fa-times mr-1"></i> Cancel
      </button>
     <button
  v-if="mode === 'provision'"
  type="button"
  class="btn btn-primary"
  :disabled="selectedFiles.length === 0 || loading || submitting"
  @click="startProvisioning"
>
  <span v-if="submitting"><i class="fas fa-sync-alt fa-spin mr-1"></i> Starting...</span>
  <span v-else><i class="fas fa-cogs mr-1"></i> Provision {{ selectedFiles.length }} File{{ selectedFiles.length !== 1 ? 's' : '' }}</span>
</button>
<button
  v-else
  type="button"
  class="btn btn-primary"
  :disabled="selectedFiles.length === 0 || loading"
  @click="confirmSelection"
>
  <i class="fas fa-check mr-1"></i> Select {{ selectedFiles.length }} File{{ selectedFiles.length !== 1 ? 's' : '' }}
</button>
    </div>

    <!-- Override footer slot to remove default Cancel/OK -->

  </modal>
</template>

<script>
import Modal from "@/views/components/theme/Modal";

export default {
  name: "ModalProvisionFileSelect",
  components: { Modal },
  props: {
    deviceId: { type: String, default: null },
    entity:   { type: String, default: null },
    mode:     { type: String, default: 'provision' },
    scopeLevel:   { type: String, default: null },
    scopeSiteId:  { type: String, default: null },
    scopeGroupId: { type: String, default: null },
  },
  data() {
    return {
      modalVisible: false,
      loading: false,
      submitting: false,
      fetchError: null,
      files: [],
      selectedFiles: [],
    };
  },
  computed: {
    allSelected()  { return this.files.length > 0 && this.selectedFiles.length === this.files.length; },
    someSelected() { return this.selectedFiles.length > 0; },
  },
  mounted() {
   if (this.mode === 'provision') {
    this.modalVisible = true;
    if (this.deviceId && this.entity) {
      this.fetchFiles();
    }
  }
  },
  methods: {
   async fetchFiles() {
    this.loading = true;
    this.fetchError = null;
    try {
        const params = {};
        if (this.scopeLevel)   params.level      = this.scopeLevel;
        if (this.scopeSiteId)  params.site_id    = this.scopeSiteId;
        if (this.scopeGroupId) params.group_id   = this.scopeGroupId;
console.log('fetchFiles scope props:', {
  scopeLevel: this.scopeLevel,
  scopeSiteId: this.scopeSiteId,
  scopeGroupId: this.scopeGroupId,
});
console.log('fetchFiles query params:', params);
        const response = await this.$http.get(
          `/tenant/${this.entity}/provision/${this.deviceId}/files`,
          { params }
        );
        this.files = response.data.files || [];
        this.selectedFiles = this.files.map(f => f.local_file);
    } catch (err) {
        this.fetchError = err.response?.data?.error || "Failed to load files. Please try again.";
    } finally {
        this.loading = false;
    }
},

    isSelected(localFile) { return this.selectedFiles.includes(localFile); },
    formatSize(bytes) {
      if (bytes === 0) return '0 B';
      if (bytes < 1024) return bytes + ' B';
      if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
      return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
    },

    toggleFile(localFile) {
      const idx = this.selectedFiles.indexOf(localFile);
      if (idx === -1) this.selectedFiles.push(localFile);
      else this.selectedFiles.splice(idx, 1);
    },

    toggleAll() {
      this.selectedFiles = this.allSelected ? [] : this.files.map(f => f.local_file);
    },

    startProvisioning() {
      if (this.selectedFiles.length === 0) return;
      this.submitting = true;
      try {
        const selectedCommands = this.files
          .filter(f => this.selectedFiles.includes(f.local_file))
          .map(f => ({ local_file: f.local_file, url: f.url }));
        this.$emit('start-provision', selectedCommands);
        this.$emit('close-modal');
      } finally {
        this.submitting = false;
      }
    },
    confirmSelection() {
  const selectedCommands = this.files
    .filter(f => this.selectedFiles.includes(f.local_file))
    .map(f => ({ local_file: f.local_file, url: f.url }));
  this.$emit('files-selected', selectedCommands);
  this.handleClose();
},
open() {
  this.modalVisible = true;
  if (this.deviceId && this.entity) {
    this.fetchFiles();
  }
},

    handleClose() {
      this.modalVisible = false;
      this.$emit('close-modal');
    },
  },
};
</script>

<style scoped>
.file-row:hover {
  background-color: #f0f4ff !important;
}
.selected-row {
  background-color: #eaf1ff !important;
}
/* Hide the Modal component built-in footer Cancel/OK */

</style>